import { isUrl } from 'support'
import isBlank from 'is-blank'
import get from 'just-safe-get'
import last from 'just-last'
import fecha from 'fecha'
import { foldazApi } from 'api'
import createFormModule from '../../utils/createFormModule'
import {
  CONTENT_FORM_REQUEST,
  CONTENT_FORM_SUCCESS,
  CONTENT_FORM_ERROR,
  FETCH_URL_REQUEST,
  FETCH_URL_SUCCESS,
  FETCH_URL_ERROR,
  SET_URL_IS_FETCHED,
  SET_SOURCE_URL,
  SET_TITLE,
  SET_BODY,
  SET_TAGS,
  SET_COUNTRIES,
  SET_AUTHOR,
  SET_SOURCE_NAME,
  SET_PUBLISHED_AT,
  SET_FOLDER_ID,
  SET_PRIORITY,
  SET_SHAREABLE,
  SET_EXTERNAL,
  SET_ATTACHMENTS,
  SET_ATTACHMENTS_DISPATCHED
} from './types'

const formModule = createFormModule({
  resourcesPath: '/contents',
  resourcesName: 'content',
  resourcePath: '/contents/:contentId',
  resourceName: 'content'
})

const REQUIRED_FIELDS = ['title', 'body', 'tags', 'priority', 'folderId']

export const state = {
  ...formModule.state,
  urlIsFetched: false,
  fetchUrlRequest: {
    isLoading: false,
    error: null
  },
  request: {
    isLoading: false,
    error: null
  },
  form: {
    id: null,
    sourceUrl: '',
    title: '',
    body: '',
    tags: [],
    countries: [],
    author: '',
    sourceName: '',
    publishedAt: '',
    folderId: '',
    priority: 'normal',
    shareable: true,
    external: true,
    attachments: [],
    attachmentsDispatched: false
  }
}

export const mutations = {
  ...formModule.mutations,
  [CONTENT_FORM_REQUEST] (state) {
    state.request.isLoading = true
  },
  [CONTENT_FORM_SUCCESS] (state) {
    state.request.isLoading = false
    state.request.error = null
  },
  [CONTENT_FORM_ERROR] (state, error) {
    state.request.isLoading = false
    state.request.error = error
  },
  [FETCH_URL_REQUEST] (state) {
    state.fetchUrlRequest.isLoading = true
  },
  [FETCH_URL_SUCCESS] (state) {
    state.fetchUrlRequest.isLoading = false
    state.fetchUrlRequest.error = null
  },
  [FETCH_URL_ERROR] (state, error) {
    state.fetchUrlRequest.isLoading = false
    state.fetchUrlRequest.error = error
  },
  [SET_URL_IS_FETCHED] (state, value) {
    state.urlIsFetched = value
  },
  [SET_SOURCE_URL] (state, value) {
    state.form.sourceUrl = value
  },
  [SET_TITLE] (state, value) {
    state.form.title = value
  },
  [SET_BODY] (state, value) {
    state.form.body = value
  },
  [SET_TAGS] (state, value) {
    state.form.tags = value
  },
  [SET_COUNTRIES] (state, value) {
    state.form.countries = value
  },
  [SET_AUTHOR] (state, value) {
    state.form.author = value
  },
  [SET_SOURCE_NAME] (state, value) {
    state.form.sourceName = value
  },
  [SET_PUBLISHED_AT] (state, value) {
    state.form.publishedAt = value
  },
  [SET_FOLDER_ID] (state, value) {
    state.form.folderId = value
  },
  [SET_PRIORITY] (state, value) {
    state.form.priority = value
  },
  [SET_SHAREABLE] (state, value) {
    state.form.shareable = value
  },
  [SET_EXTERNAL] (state, value) {
    state.form.external = value
  },
  [SET_ATTACHMENTS] (state, value) {
    state.form.attachments = value
  },
  [SET_ATTACHMENTS_DISPATCHED] (state, value) {
    state.form.attachmentsDispatched = value
  }
}

export const actions = {
  ...formModule.actions,
  setUrlIsFetched ({ commit }, value) {
    commit(SET_URL_IS_FETCHED, value)
  },
  setSourceUrl ({ commit }, value) {
    commit(SET_SOURCE_URL, value)
  },
  setTitle ({ commit }, value) {
    commit(SET_TITLE, value)
  },
  setBody ({ commit }, value) {
    commit(SET_BODY, value)
  },
  setTags ({ commit }, value) {
    const tags = value.map(t => {
      return t.id
        ? { label: t.name, value: t.name }
        : { label: t.label, value: t.value }
    })
    commit(SET_TAGS, tags)
  },
  setCountries ({ commit }, value) {
    const countries = value.map(t => {
      return t.id
        ? { label: t.label, value: t.name }
        : { label: t.label, value: t.value }
    })
    commit(SET_COUNTRIES, countries)
  },
  setAuthor ({ commit }, value) {
    commit(SET_AUTHOR, value)
  },
  setSourceName ({ commit }, value) {
    commit(SET_SOURCE_NAME, value)
  },
  setPublishedAt ({ commit }, value) {
    let normalizedValue
    if (value && value.constructor === Number) {
      const date = new Date(0)
      date.setUTCSeconds(value)
      normalizedValue = fecha.format(date, 'YYYY-MM-DD')
    } else if (value && value.constructor === String) {
      const split = value.split('T')
      if (split.length === 2) {
        normalizedValue = split[0]
      } else {
        normalizedValue = value
      }
    }
    commit(SET_PUBLISHED_AT, normalizedValue)
  },
  setFolderId ({ commit }, value) {
    commit(SET_FOLDER_ID, value)
  },
  setPriority ({ commit }, value) {
    commit(SET_PRIORITY, value)
  },
  setShareable ({ commit }, value) {
    commit(SET_SHAREABLE, value)
  },
  setExternal ({ commit }, value) {
    commit(SET_EXTERNAL, value)
  },
  setAttachments ({ commit, dispatch, state }, value ) {
    if (!state.form.attachmentsDispatched && value.length) {
      const attachments = value.map((attachment) => {
        attachment.src = attachment.url
        return attachment
      })
      dispatch('contentAttachments/set', attachments, { root: true })
        .then(() => {
          commit(SET_ATTACHMENTS_DISPATCHED, true)
        })
    }
    commit(SET_ATTACHMENTS, value)
  },
  setAttachmentsDispatched ({ commit }, value ) {
    commit(SET_ATTACHMENTS_DISPATCHED, value)
  },
  fetchUrlData ({ commit, dispatch, state }) {
    const { sourceUrl, folderId } = state.form
    dispatch('reset')
    commit(FETCH_URL_REQUEST)

    if (isUrl(sourceUrl)) {
      return foldazApi.get('/embedly', { params: { url: sourceUrl } })
        .then(({ data }) => {
          const attachments = data.images.map(({ url }) => {
            const originalFilename = last(url.split('/')).split('?')[0]
            let contentType
            switch (last(originalFilename.split('.'))) {
              case 'jpg':
                contentType = 'image/jpeg'
              case 'jpeg':
                contentType = 'image/jpeg'
              case 'gif':
                contentType = 'image/gif'
              case 'png':
                contentType = 'image/png'
            }
            return { src: url, contentType, originalFilename, isRemote: true }
          })
          dispatch('setSourceUrl', sourceUrl)
          dispatch('setfolderId', folderId)
          dispatch('contentAttachments/set', attachments, { root: true })
          dispatch('setTitle', data.title)

          const content = get(data, 'media.html') ? data.media.html : data.content
          dispatch('setBody', content)

          if (data.authors && data.authors.length > 0) {
            dispatch('setAuthor', data.authors[0].name)
          }
          if (data.sourceName) {
            dispatch('setSourceName', data.sourceName)
          }
          if (data.publishedAt) {
            dispatch('setPublishedAt', data.publishedAt / 1000)
          }
          dispatch('setUrlIsFetched', true)
          commit(FETCH_URL_SUCCESS)
        })
        .catch(error => {
          commit(FETCH_URL_ERROR, error)
          throw error
        })
    }
    return Promise.resolve()
  },
  customSubmit ({ commit, dispatch, state }) {
    commit(CONTENT_FORM_REQUEST)
    const contentId = state.form.id
    const promise = isBlank(contentId)
      ? dispatch('create')
      : dispatch('update', { contentId })

    return promise
      .then(({ data }) => commit('SET_CONTENT', data.content, { root: true }))
      .then(() => dispatch('submitAttachments'))
  },
  submitAttachments ({ commit, dispatch }) {
    return dispatch('contentAttachments/submit', null, { root: true })
      .then(() => commit(CONTENT_FORM_SUCCESS))
      .catch(error => commit(CONTENT_FORM_ERROR, error))
  },
  prefill ({ dispatch, state, rootState }) {
    if (isBlank(state.form.folderId)) {
      dispatch('setFolderId', rootState.route.params.folderId)
    }

    if (isBlank(state.form.id)) {
      const now = new Date()
      const today = now.getFullYear() + '-' + ('0' + (now.getMonth() + 1)).slice(-2) + '-' + ('0' + now.getDate()).slice(-2)
      dispatch('setPublishedAt', today)
    }
  },
  reset ({ dispatch }) {
    dispatch('setSourceUrl', '')
    dispatch('setUrlIsFetched', false)
    dispatch('setId', null)
    dispatch('setTitle', '')
    dispatch('setBody', '')
    dispatch('setTags', [])
    dispatch('setCountries', [])
    dispatch('setAuthor', '')
    dispatch('setSourceName', '')
    dispatch('setPublishedAt', '')
    dispatch('setPriority', 'normal')
    dispatch('setShareable', true)
    dispatch('setExternal', true)
    dispatch('setAttachments', [])
    dispatch('setAttachmentsDispatched', false)
    dispatch('contentAttachments/set', [], { root: true })
  }
}

export const getters = {
  ...formModule.getters,
  isSaveable ({ form }) {
    const blankFields = REQUIRED_FIELDS.filter(field => isBlank(form[field]))
    return blankFields.length <= 0
  },
  priorityOptions () {
    return [
      { label: 'Normal', value: 'normal' },
      { label: 'Hoch', value: 'high' }
    ]
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/contents/contentForm.js